import React, { useRef } from "react";
import styled from "styled-components";

const OtpContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  width: 100%;
  height: 72px;
  padding-bottom: 8px;
`;

const Label = styled.span`
  color: rgb(33, 37, 67);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const OtpBox = styled.input`
  width: 48px;
  height: 42px;
  text-align: center;
  font-size: 18px;
  border: 1px solid
    ${(props) => (props.error ? "rgb(220, 38, 38)" : "rgb(224, 228, 244)")};
  border-radius: 8px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  color: rgb(33, 37, 67);
  background: white;
  transition: border-color 0.2s;

  &:focus {
    border-color: rgb(97, 98, 197);
    outline: none;
  }

  &::placeholder {
    color: rgb(179, 182, 195);
  }
`;

const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
  text-align: center;
`;

const Required = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
`;

const OtpInput = ({
  label,
  value = "",
  onChange,
  length = 6,
  error,
  errorMessage,
  required,
}) => {
  const inputsRef = useRef([]);

  const handleChange = (e, index) => {
    const val = e.target.value.replace(/\D/, "");
    if (!val) return;

    const newValue = value.split("");
    newValue[index] = val;
    const joined = newValue.join("").slice(0, length);
    onChange(joined);

    if (index < length - 1 && inputsRef.current[index + 1]) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);

    if (pastedData.length > 1) {
      onChange(pastedData);
      // focus the last filled box
      const lastIndex = Math.min(pastedData.length, length) - 1;
      if (inputsRef.current[lastIndex]) {
        inputsRef.current[lastIndex].focus();
      }
      e.preventDefault();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newValue = value.split("");

      if (value[index]) {
        // If current box has a value, just clear it
        newValue[index] = "";
        onChange(newValue.join(""));
      } else if (index > 0) {
        // If empty, move to previous box and clear it
        newValue[index - 1] = "";
        onChange(newValue.join(""));
        inputsRef.current[index - 1].focus();
      }

      e.preventDefault(); // prevent default backspace behavior
    }
  };

  return (
    <OtpContainer>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}
      <InputGroup>
        {Array.from({ length }).map((_, index) => (
          <OtpBox
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value[index] || ""}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            ref={(el) => (inputsRef.current[index] = el)}
            error={error}
            onPaste={handlePaste}
          />
        ))}
      </InputGroup>
      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </OtpContainer>
  );
};

export default OtpInput;
